/* global document */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { func, object, string } from 'prop-types';
import { IoMdClose } from 'react-icons/io';
import { createPortal } from 'react-dom';

import ShareIconList from './ShareIconList';
import ProductGallery from './ProductGallery';
import ProductAddToCartFormSkeleton from './ProductAddToCartFormSkeleton';
import ProductAddToCartForm from './ProductAddToCartForm';
import { getMatchingVariant, fetchProductData } from './Product';
import AppContext from './context/AppContext';
import { LoadingContext } from './context/LoadingContext';
import { CartContext } from './context/CartContext';
import usePrefetch from '../hooks/usePrefetch';
import { ToastContext } from './context/ToastContext';

import '../styles/ProductQuickView.css';

const ProductQuickView = ({ handle, handleToggle, OverlayTriggerProps }) => {
    /** Contexts */
    const { appContext } = useContext(AppContext);
    const appConfig = appContext.config.themeConfig;
    const { cartState } = useContext(CartContext);
    const { startLoading, finishLoading } = useContext(LoadingContext);
    const [stateLoaded, setStateLoaded] = useState(false);
    const { sendMessage } = useContext(ToastContext);
    const viewContentRef = useRef(null);

    /** States */
    const [ maxQuantity, setMaxQuantity ] = useState(0);
    const [ price, setPrice ] = useState(0);
    const [ selectedOptions, setSelectedOptions ] = useState(new Map());
    const [ selectedVariant, setSelectedVariant ] = useState(null);
    const [ totalCartQuantity, setTotalCartQuantity ] = useState(0);
    const [container, setContainer] = useState(null);

    // Pre-fetch product data.
    const { isLoaded: productLoaded, data: product, error } = usePrefetch({
        key: 'product',
        resourceKey: 'handle',
        resourceValue: handle,
        prefetchFn: async () => {
            await startLoading();
            const data = await fetchProductData(handle);
            await finishLoading();
            return data;
        },
    });


    // Load product data
    useEffect(() => {
        if (!product) {
            return;
        }
        if (error) {
            sendMessage(error.message, 'error');
            return;
        }
        const { Variants: variants } = product;
        if (variants.length === 1) {
            const defaultVariant = variants.at(0);
            setPrice(defaultVariant.price);
            setSelectedVariant(defaultVariant);
        } else {
            setPrice(product.price);
            setSelectedVariant(null);
        }
        setSelectedOptions(new Map());
        setStateLoaded(true);
    }, [
        error,
        product,
    ]);

    // Update total & max. qty on cart state changes
    useEffect(() => {
        let cartQty = 0;
        let maxQty = 0;
        const variant = selectedVariant ?? (product?.Variants.length === 1
            ? product?.Variants.at(0)
            : product?.Variants.find(
                (variant) => variant.title === 'Default Title'
                    || variant.variantPosition === 1
            )
        );
        if (variant) {
            maxQty = variant.availableQuantity;
            const inventoryLevels = variant.availableInventoryLevels.filter((l) => l.variantId === variant.id);
            inventoryLevels.forEach((inventoryLevel) => {
                if (inventoryLevel.bypassNegativeQuantity === true) {
                    maxQty = Infinity;
                }
                const cartItem = cartState.find((i) => i.inventoryLevelId === inventoryLevel.id);
                if (cartItem) {
                    if (maxQty !== Infinity) {
                        maxQty -= cartItem.quantity;
                    }
                    cartQty += cartItem.quantity;
                }
            });
        }
        setMaxQuantity(maxQty);
        setTotalCartQuantity(cartQty);
    }, [selectedVariant, cartState, product]);

    /* Determine whether or not all options have been selected */
    const isAllOptionsSelected = useMemo(() => {
        if (!productLoaded
            || !product
            || !selectedOptions.size)
            return false;
        const { Options: options } = product;
        return options.every((o) => selectedOptions.get(o.id) !== undefined);
    }, [productLoaded, product, selectedOptions]);

    /* Set selected option */
    const selectOption = useCallback((optionId, value) => {
        if (!productLoaded
            || !product)
            return;
        const currentValue = selectedOptions.get(optionId);
        if (!currentValue && value === '')
            return;
        const newSelectedOptions = new Map(selectedOptions);
        if (value === '') {
            newSelectedOptions.delete(optionId);
        } else {
            newSelectedOptions.set(optionId, value);
        }
        setSelectedOptions(newSelectedOptions);
        const selectedVariant = getMatchingVariant(product.Variants, newSelectedOptions);
        setSelectedVariant(selectedVariant);
        if (selectedVariant !== null) {
            setPrice(selectedVariant.price);
        }
    }, [productLoaded, product, selectedOptions, getMatchingVariant]);

    const viewRef = useCallback(node => {
        if (!node) {
            return;
        }

        const handleClickOutside = (e) => {
            if (!viewContentRef.current.contains(e.target)) {
                handleToggle(false);
            }
        };

        node.addEventListener('click', handleClickOutside);
    }, []);

    useEffect(() => {
        setContainer(typeof window !== 'undefined' ? document.getElementById('root') : null);
    }, []);


    if (!product || container === null) {
        return null;
    }

    const { Images, seoDescription } = product;
    
    return createPortal(
        <div
            {...OverlayTriggerProps}
            className={'product-quick-view'}
            onClick={e => e.stopPropagation()}
            ref={viewRef}
        >
            <div 
                className={'product-quick-view__content'}
                ref={viewContentRef}
            >
                <IoMdClose
                    onClick={() => handleToggle(false)}
                    className={'product-quick-view__close-icon'}
                    size={'25px'}/>
                <ProductGallery
                    product={product}
                    images={Images}
                />
                <div
                    className={'product-quick-view__cta'}
                >
                    <p
                        className={'product-quick-view__description'}
                    >
                        {seoDescription}
                    </p>
                    <div
                        className={'product-quick-view__icons-container'}
                    ><ShareIconList
                            iconSize={'24px'}/></div>
                    <div>
                        <span
                            className={'product-quick-view__price'}>Price: </span> 
                        <span
                            className={'product-quick-view__amount'}>${(Math.round(price * 100) / 100).toFixed(2)} {appConfig.storeCurrency}</span>
                    </div>
                    {!stateLoaded && <ProductAddToCartFormSkeleton />}
                    {!!stateLoaded && <ProductAddToCartForm
                        isProductAvailableForSale={product.availableForSale}
                        isAllOptionsSelected={isAllOptionsSelected}
                        maxQuantity={maxQuantity}
                        options={product.Options}
                        price={price}
                        selectedOptions={selectedOptions}
                        selectOption={selectOption}
                        selectedVariant={selectedVariant}
                        variants={product.Variants}
                        totalCartQuantity={totalCartQuantity}
                    />}
                </div>
            </div>
        </div>,
        container
    );
};

ProductQuickView.propTypes = {
    handle: string,
    handleToggle: func,
    OverlayTriggerProps: object,
};

export default ProductQuickView;